import type { PendingShareEntry } from '@/api/share';
import * as shareApi from '@/api/share';
import {
    CheckCircleFilled,
    CloseCircleFilled,
    DownOutlined,
    RightOutlined,
} from '@ant-design/icons';
import { Button, message, Spin } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ShareNotificationPanel: React.FC = () => {
    const { t } = useTranslation('common');
    const [collapsed, setCollapsed] = useState(false);
    const [pendingShares, setPendingShares] = useState<PendingShareEntry[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pendingActionShareLink, setPendingActionShareLink] = useState<string | null>(null);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const loadPendingShares = useCallback(async () => {
        setIsLoading(true);
        try {
            const pendingRes = await shareApi.getShareWithMePending();
            setPendingShares(pendingRes?.ok && pendingRes.data ? pendingRes.data : []);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPendingShares();
        intervalRef.current = setInterval(loadPendingShares, 30000);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [loadPendingShares]);

    const handlePendingShareAction = async (
        entry: PendingShareEntry,
        action: 'accept' | 'reject'
    ) => {
        setPendingActionShareLink(entry.share_link);
        try {
            const response = action === 'accept'
                ? await shareApi.acceptShare({ shareId: entry.share_link })
                : await shareApi.rejectShare({ shareId: entry.share_link });

            if (response?.ok) {
                message.success(
                    action === 'accept'
                        ? t('accept_share.success', { defaultValue: 'Successfully accepted the shared file!' })
                        : t('accept_share.reject_success', { defaultValue: 'Share invitation rejected' })
                );
                await loadPendingShares();
                return;
            }

            message.error(
                response?.message
                || (action === 'accept'
                    ? t('accept_share.failed', { defaultValue: 'Failed to accept share' })
                    : t('accept_share.reject_failed', { defaultValue: 'Failed to reject share invitation' }))
            );
        } catch {
            message.error(
                action === 'accept'
                    ? t('accept_share.failed', { defaultValue: 'Failed to accept share' })
                    : t('accept_share.reject_failed', { defaultValue: 'Failed to reject share invitation' })
            );
        } finally {
            setPendingActionShareLink(null);
        }
    };

    if (pendingShares.length === 0) return null;

    return (
        <div className="border-t border-gray-200 bg-neutral-light shrink-0">
            <div
                className="flex items-center justify-between px-2 py-1 cursor-pointer hover:bg-gray-100 select-none"
                onClick={() => setCollapsed(!collapsed)}
            >
                <div className="flex items-center gap-1 min-w-0">
                    {collapsed ? <RightOutlined className="text-[10px]" /> : <DownOutlined className="text-[10px]" />}
                    <span className="text-xs font-medium text-amber-700 truncate">
                        {t('share_with_me.pending_title', { defaultValue: 'Pending Invitations' })}
                    </span>
                    <span className="text-[10px] bg-amber-500 text-white rounded-full px-1.5 leading-4 shrink-0">
                        {pendingShares.length}
                    </span>
                </div>
                {isLoading && <Spin size="small" className="shrink-0" />}
            </div>

            {!collapsed && (
                <div className="px-2 pb-1 max-h-32 overflow-y-auto">
                    {pendingShares.map((entry) => {
                        const isBusy = pendingActionShareLink === entry.share_link;
                        return (
                            <div
                                key={`pending-${entry.id}`}
                                className="flex items-center justify-between gap-2 py-1 border-t border-gray-100 first:border-t-0"
                            >
                                <span className="min-w-0 truncate text-xs" title={`${entry.file_name} (${entry.sharing_user})`}>
                                    {`${entry.file_name}(${entry.sharing_user})`}
                                </span>
                                <div className="flex items-center gap-0.5 shrink-0">
                                    <Button
                                        type="text"
                                        size="small"
                                        title={t('accept_share.accept', { defaultValue: 'Accept and Save' })}
                                        icon={<CheckCircleFilled className="text-green-600" />}
                                        loading={isBusy}
                                        disabled={isBusy}
                                        onClick={() => handlePendingShareAction(entry, 'accept')}
                                    />
                                    <Button
                                        type="text"
                                        size="small"
                                        title={t('accept_share.reject', { defaultValue: 'Reject' })}
                                        icon={<CloseCircleFilled className="text-red-500" />}
                                        loading={isBusy}
                                        disabled={isBusy}
                                        onClick={() => handlePendingShareAction(entry, 'reject')}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ShareNotificationPanel;
